/* Arc & Day: native Shopify storefront interactions. No credentials or framework runtime. */
(() => {
  "use strict";
  document.documentElement.classList.replace("no-js", "js");
  const strings = JSON.parse(
    document.getElementById("ArcStrings")?.textContent || "{}",
  );
  const root = document.body.dataset.root || "/";
  const savedKey = "arc-day-saved-handles-v1";
  const state = { cartBusy: false, cartRead: 0, savedRead: 0, quickRead: 0 };
  const openers = new WeakMap();
  const $ = (selector, context = document) => context.querySelector(selector);
  const $$ = (selector, context = document) => [
    ...context.querySelectorAll(selector),
  ];
  const status = (message) => {
    const node = $("#ArcStatus");
    if (node) node.textContent = message;
  };
  const localURL = (value) => {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin) throw Error("Invalid destination");
    return url;
  };
  async function fetchText(url, options = {}) {
    const response = await fetch(localURL(url), {
      credentials: "same-origin",
      signal: AbortSignal.timeout(20000),
      ...options,
    });
    if (!response.ok) throw Error(strings.error);
    return response.text();
  }
  function parseHTML(html) {
    return new DOMParser().parseFromString(html, "text/html");
  }
  function uniqueIds(node, prefix) {
    const ids = new Map();
    $$("[id]", node).forEach((element) => {
      ids.set(element.id, prefix + element.id);
      element.id = prefix + element.id;
    });
    $$(
      "[for],[form],[aria-labelledby],[aria-describedby],[aria-controls]",
      node,
    ).forEach((element) => {
      [
        "for",
        "form",
        "aria-labelledby",
        "aria-describedby",
        "aria-controls",
      ].forEach((attribute) => {
        if (element.hasAttribute(attribute))
          element.setAttribute(
            attribute,
            element
              .getAttribute(attribute)
              .split(" ")
              .map((id) => ids.get(id) || id)
              .join(" "),
          );
      });
    });
    return node;
  }
  function openDialog(id, opener = document.activeElement) {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    openers.set(dialog, opener);
    dialog.showModal();
    document.body.classList.add("dialog-open");
    (
      dialog.querySelector("input[type=search]") ||
      dialog.querySelector("[data-close]")
    )?.focus();
  }
  function closeDialog(dialog) {
    if (dialog?.open) dialog.close();
  }
  function initDialogs(scope = document) {
    $$("dialog", scope).forEach((dialog) => {
      if (dialog.dataset.initialized) return;
      dialog.dataset.initialized = "true";
      dialog.addEventListener("close", () => {
        if (!$("dialog[open]")) document.body.classList.remove("dialog-open");
        const opener = openers.get(dialog);
        if (opener?.isConnected) opener.focus();
      });
      dialog.addEventListener("click", (event) => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          closeDialog(dialog);
      });
    });
  }
  function saved() {
    try {
      return [...new Set(JSON.parse(localStorage.getItem(savedKey) || "[]"))]
        .filter(
          (value) => typeof value === "string" && /^[a-z0-9-]+$/.test(value),
        )
        .slice(0, 40);
    } catch {
      return [];
    }
  }
  function syncSaved() {
    const handles = saved();
    $$("[data-save]").forEach((button) => {
      const selected = handles.includes(button.dataset.save);
      button.setAttribute("aria-pressed", String(selected));
      const label = $("[data-save-text]", button);
      if (label)
        label.textContent = selected
          ? label.dataset.savedText
          : label.dataset.saveText;
    });
    $$("[data-saved-count]").forEach((node) => {
      node.textContent = String(handles.length);
      node.hidden = handles.length === 0;
    });
  }
  function errorWithRetry(container, message, retry) {
    container.replaceChildren();
    const p = document.createElement("p");
    p.setAttribute("role", "alert");
    p.textContent = message;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "button secondary";
    button.textContent = strings.retry;
    button.addEventListener("click", retry, { once: true });
    container.append(p, button);
  }
  async function renderSaved() {
    const container = $("#SavedContent");
    if (!container) return;
    const generation = ++state.savedRead;
    const handles = saved();
    container.textContent = strings.loading;
    if (!handles.length) {
      container.textContent = strings.savedEmpty;
      return;
    }
    try {
      const cards = await Promise.all(
        handles.map(async (handle) => {
          const html = await fetchText(
            root +
              "products/" +
              encodeURIComponent(handle) +
              "?section_id=product-card-fragment",
          );
          return parseHTML(html).querySelector(".product-card");
        }),
      );
      if (generation !== state.savedRead) return;
      container.replaceChildren();
      const grid = document.createElement("div");
      grid.className = "product-grid saved-grid";
      cards.forEach((card, i) => {
        if (card) grid.append(card);
        else {
          const unavailable = document.createElement("div");
          const p = document.createElement("p");
          p.textContent = "This saved piece is no longer available.";
          const button = document.createElement("button");
          button.type = "button";
          button.dataset.save = handles[i];
          button.className = "text-link";
          button.textContent = "Remove saved piece";
          unavailable.append(p, button);
          grid.append(unavailable);
        }
      });
      container.append(grid);
      syncSaved();
    } catch {
      if (generation === state.savedRead)
        errorWithRetry(container, strings.error, renderSaved);
    }
  }
  async function quickView(url, opener) {
    const generation = ++state.quickRead;
    openDialog("QuickDialog", opener);
    const container = $("#QuickContent");
    container.textContent = strings.loading;
    try {
      // Fetch the actual product template first so merchant-edited blocks and
      // app content are retained. Subsequent option changes render its section.
      const requestURL = localURL(url);
      const doc = parseHTML(await fetchText(requestURL));
      const product = doc.querySelector("arc-product");
      if (!product) throw Error();
      if (generation !== state.quickRead) return;
      product.dataset.quick = "true";
      const heading = product.querySelector("h1");
      if (heading) {
        const h2 = document.createElement("h2");
        h2.className = heading.className;
        h2.textContent = heading.textContent;
        heading.replaceWith(h2);
      }
      uniqueIds(product, "Quick-");
      product.querySelector(".quick-full-details")?.removeAttribute("hidden");
      container.replaceChildren(product);
      init(product);
    } catch {
      if (generation === state.quickRead)
        errorWithRetry(container, strings.error, () => quickView(url, opener));
    }
  }
  function cartError(message = "") {
    $$("[data-cart-error]").forEach((node) => {
      node.textContent = message;
      node.hidden = !message;
    });
    if (message) status(message);
  }
  function cartBusy(busy) {
    state.cartBusy = busy;
    $$("[data-cart-view]").forEach((node) =>
      node.setAttribute("aria-busy", String(busy)),
    );
    $$("[data-cart-write],.add-button").forEach((control) => {
      if (busy) {
        control.dataset.wasDisabled = String(control.disabled);
        control.disabled = true;
      } else if (control.hasAttribute("data-was-disabled")) {
        control.disabled = control.dataset.wasDisabled === "true";
        delete control.dataset.wasDisabled;
      }
    });
  }
  function renderCart(html) {
    const fragment = parseHTML(html).querySelector("[data-cart-fragment]");
    if (!fragment) throw Error("Missing cart section");
    $$("[data-cart-view]").forEach((view) => {
      const next = uniqueIds(fragment.cloneNode(true), view.id + "-");
      view.replaceChildren(next);
    });
    $$("[data-cart-count]").forEach((node) => {
      node.textContent = fragment.dataset.count;
    });
  }
  async function refreshCart() {
    if (state.cartBusy) return;
    const generation = ++state.cartRead;
    cartError("");
    $$("[data-cart-view]").forEach((node) =>
      node.setAttribute("aria-busy", "true"),
    );
    try {
      const html = await fetchText(root + "cart?section_id=cart-panel");
      if (generation === state.cartRead && !state.cartBusy) renderCart(html);
    } catch {
      if (generation === state.cartRead) cartError(strings.cartError);
    } finally {
      if (generation === state.cartRead)
        $$("[data-cart-view]").forEach((node) =>
          node.removeAttribute("aria-busy"),
        );
    }
  }
  async function mutateCart(endpoint, data, added = false) {
    if (state.cartBusy) return;
    ++state.cartRead;
    cartBusy(true);
    cartError("");
    try {
      const response = await fetch(root + endpoint, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          ...data,
          sections: ["cart-panel"],
          sections_url: location.pathname,
        }),
        signal: AbortSignal.timeout(20000),
      });
      const result = await response.json();
      if (!response.ok)
        throw Error(
          typeof result.description === "string"
            ? result.description
            : typeof result.message === "string"
              ? result.message
              : strings.cartError,
        );
      if (result.sections?.["cart-panel"])
        renderCart(result.sections["cart-panel"]);
      else renderCart(await fetchText(root + "cart?section_id=cart-panel"));
      if (added) {
        closeDialog($("#QuickDialog"));
        if (document.body.dataset.cartDrawer === "true")
          openDialog("CartDialog");
        else location.assign(document.body.dataset.cartUrl || root + "cart");
      }
      status(added ? strings.added : strings.updated);
    } catch (error) {
      // A response can be lost after Shopify commits a write. Reconcile, never repeat it automatically.
      try {
        renderCart(await fetchText(root + "cart?section_id=cart-panel"));
      } catch {
        /* keep the last known cart visible */
      }
      cartError(error.message || strings.cartError);
      if (added) {
        closeDialog($("#QuickDialog"));
        openDialog("CartDialog");
      }
    } finally {
      cartBusy(false);
    }
  }
  class ArcGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.generation = 0;
      this.addEventListener("click", (event) => {
        const thumbnail = event.target.closest("[data-image]");
        if (thumbnail) this.selectImage(thumbnail);
      });
      this.addEventListener("keydown", (event) => {
        const thumbnail = event.target.closest("[data-image]");
        if (
          !thumbnail ||
          !["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)
        )
          return;
        event.preventDefault();
        const thumbnails = $$("[data-image]", this);
        const index = thumbnails.indexOf(thumbnail);
        const next =
          event.key === "Home"
            ? 0
            : event.key === "End"
              ? thumbnails.length - 1
              : (index +
                  (event.key === "ArrowRight" ? 1 : -1) +
                  thumbnails.length) %
                thumbnails.length;
        thumbnails[next].focus({ preventScroll: true });
        thumbnails[next].scrollIntoView({
          block: "nearest",
          inline: "nearest",
        });
        this.selectImage(thumbnails[next]);
      });
    }
    async selectImage(thumbnail) {
      const image = $(".main-product-image", this);
      if (!thumbnail || !image) return;
      const generation = ++this.generation;
      const error = $(".gallery-error", this);
      error.hidden = true;
      this.setAttribute("aria-busy", "true");
      const preload = new Image();
      let timeout;
      try {
        // Keep the current photo in place until its replacement is decoded.
        // Only the latest selection may update the gallery after a slow load.
        preload.src = thumbnail.dataset.image;
        await Promise.race([
          preload.decode(),
          new Promise((_, reject) => {
            timeout = setTimeout(() => reject(Error("Image timeout")), 15000);
          }),
        ]);
        if (generation !== this.generation || !this.isConnected) return;
        image.removeAttribute("srcset");
        image.src = preload.src;
        image.alt = thumbnail.dataset.imageAlt || "";
        image.width = preload.naturalWidth;
        image.height = preload.naturalHeight;
        $$("[data-image]", this).forEach((item) =>
          item.setAttribute("aria-pressed", String(item === thumbnail)),
        );
      } catch {
        if (generation === this.generation && this.isConnected)
          error.hidden = false;
      } finally {
        clearTimeout(timeout);
        if (generation === this.generation) this.removeAttribute("aria-busy");
      }
    }
  }
  customElements.define("arc-gallery", ArcGallery);
  class ArcProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.addEventListener("change", (event) => {
        if (event.target.matches("[data-option-id]"))
          this.selectOptions(event.target);
      });
    }
    disconnectedCallback() {
      this.request?.abort();
    }
    async selectOptions(input) {
      this.request?.abort();
      const controller = new AbortController();
      this.request = controller;
      const selected = $$("[data-option-id]:checked", this).map(
        (option) => option.dataset.optionId,
      );
      const url = localURL(input.dataset.productUrl || this.dataset.productUrl);
      url.searchParams.set("option_values", selected.join(","));
      url.searchParams.set("section_id", this.dataset.sectionId);
      this.setAttribute("aria-busy", "true");
      const error = $("[data-product-error]", this);
      error.hidden = true;
      $$(".add-button", this).forEach((button) => {
        button.disabled = true;
      });
      try {
        const html = await fetchText(url, {
          signal: AbortSignal.any([
            controller.signal,
            AbortSignal.timeout(20000),
          ]),
        });
        if (controller.signal.aborted) return;
        const next = parseHTML(html).querySelector("arc-product");
        if (!next) throw Error();
        const isQuick = this.dataset.quick === "true";
        if (isQuick) {
          uniqueIds(next, "Quick-");
          const heading = next.querySelector("h1");
          if (heading) {
            const h2 = document.createElement("h2");
            h2.className = heading.className;
            h2.textContent = heading.textContent;
            heading.replaceWith(h2);
          }
          next.querySelector(".quick-full-details")?.removeAttribute("hidden");
        }
        const gallery = $("arc-gallery", this);
        const nextGallery = $("arc-gallery", next);
        let variantImage;
        if (
          gallery &&
          nextGallery &&
          gallery.dataset.productId === nextGallery.dataset.productId
        ) {
          if (gallery.dataset.variantImage !== nextGallery.dataset.variantImage)
            variantImage = nextGallery.dataset.variantImage;
          gallery.dataset.variantImage = nextGallery.dataset.variantImage;
          // Reuse the gallery so size changes do not reset the viewed photo,
          // reload every thumbnail, or lose its horizontal scroll position.
          nextGallery.replaceWith(gallery);
        }
        this.replaceChildren(...next.childNodes);
        if (variantImage)
          gallery.selectImage(
            $$("[data-image]", gallery).find(
              (item) => item.dataset.imageId === variantImage,
            ),
          );
        this.dataset.selectedVariant = next.dataset.selectedVariant;
        this.dataset.productUrl = next.dataset.productUrl;
        const focus = $$("[data-option-id]", this).find(
          (option) => option.dataset.optionId === input.dataset.optionId,
        );
        focus?.focus({ preventScroll: true });
        if (!isQuick) {
          const historyURL = localURL(this.dataset.productUrl);
          if (next.dataset.selectedVariant)
            historyURL.searchParams.set(
              "variant",
              next.dataset.selectedVariant,
            );
          else historyURL.searchParams.set("option_values", selected.join(","));
          history.replaceState({}, "", historyURL.pathname + historyURL.search);
        }
        syncSaved();
        initTables(this);
      } catch {
        if (!controller.signal.aborted) {
          error.hidden = false;
          error.textContent = strings.optionsError + " ";
          const retry = document.createElement("button");
          retry.type = "button";
          retry.className = "text-link";
          retry.textContent = strings.retry;
          retry.addEventListener("click", () => this.selectOptions(input), {
            once: true,
          });
          error.append(retry);
          status(strings.optionsError);
        }
      } finally {
        if (this.request === controller) this.removeAttribute("aria-busy");
      }
    }
  }
  customElements.define("arc-product", ArcProduct);
  let predictiveController, predictiveTimer;
  async function predictive(input) {
    const output = $("#SearchSuggestions");
    predictiveController?.abort();
    const query = input.value.trim();
    if (query.length < 2) {
      output.replaceChildren();
      input.setAttribute("aria-expanded", "false");
      return;
    }
    const controller = new AbortController();
    predictiveController = controller;
    output.textContent = strings.loading;
    input.setAttribute("aria-expanded", "true");
    try {
      const url =
        root +
        "search/suggest?q=" +
        encodeURIComponent(query) +
        "&resources[type]=product&resources[limit]=4&section_id=predictive-search";
      const doc = parseHTML(
        await fetchText(url, {
          signal: AbortSignal.any([
            controller.signal,
            AbortSignal.timeout(15000),
          ]),
        }),
      );
      if (controller.signal.aborted) return;
      const results = doc.querySelector("#PredictiveResults");
      output.replaceChildren();
      if (results) output.append(results);
      else
        output.textContent =
          "Search the full collection using the search button.";
    } catch {
      if (!controller.signal.aborted)
        output.textContent =
          "Suggestions could not be loaded. You can still search the full collection.";
    }
  }
  function initTables(scope) {
    $$(".product-description-text p, .product-description-text div", scope)
      .reverse()
      .forEach((element) => {
        if (
          !element.textContent.trim() &&
          !element.querySelector("img, video, iframe, table, svg")
        )
          element.remove();
      });
    $$(".rich-content table", scope).forEach((table) => {
      if (table.parentElement.classList.contains("detail-table")) return;
      const wrapper = document.createElement("div");
      wrapper.className = "detail-table";
      wrapper.tabIndex = 0;
      wrapper.setAttribute("role", "region");
      wrapper.setAttribute("aria-label", "Product information table");
      table.replaceWith(wrapper);
      wrapper.append(table);
    });
  }
  function init(scope = document) {
    initDialogs(scope);
    syncSaved();
    initTables(scope);
  }
  document.addEventListener("click", (event) => {
    const button = event.target.closest("button,a");
    if (!button) return;
    if (
      button.matches('a[href="#shopifyReshowConsentBanner"]') &&
      window.privacyBanner?.showPreferences
    ) {
      event.preventDefault();
      window.privacyBanner.showPreferences().catch(() => status(strings.error));
    } else if (button.matches("[data-open]")) {
      event.preventDefault();
      openDialog(button.dataset.open, button);
      if (button.dataset.open === "CartDialog") refreshCart();
      if (button.dataset.open === "SavedDialog") renderSaved();
    } else if (button.matches("[data-close]"))
      closeDialog(button.closest("dialog"));
    else if (button.matches("[data-top]")) {
      window.scrollTo({
        top: 0,
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
    } else if (button.matches("[data-save]")) {
      event.preventDefault();
      const feedback = button
        .closest("arc-product")
        ?.querySelector("[data-save-feedback]");
      if (feedback) feedback.hidden = true;
      const handles = saved(),
        handle = button.dataset.save;
      const next = handles.includes(handle)
        ? handles.filter((value) => value !== handle)
        : [...handles, handle].slice(-40);
      try {
        localStorage.setItem(savedKey, JSON.stringify(next));
      } catch {
        if (feedback) feedback.hidden = false;
        status("Saved pieces could not be stored on this device.");
        return;
      }
      syncSaved();
      status(
        next.includes(handle)
          ? "Piece saved."
          : "Piece removed from saved pieces.",
      );
      if ($("#SavedDialog")?.open) renderSaved();
    } else if (button.matches("[data-quick]")) {
      event.preventDefault();
      quickView(button.dataset.quick, button);
    } else if (button.matches("[data-quantity]")) {
      const input = $("input", button.closest(".quantity-control"));
      if (button.dataset.quantity === "plus") input.stepUp();
      else input.stepDown();
      input.dispatchEvent(new Event("change", { bubbles: true }));
    } else if (button.matches("[data-zoom]")) {
      const image = $("img", button);
      if (!image) return;
      const zoom = $("#ZoomImage");
      zoom.src = image.currentSrc || image.src;
      zoom.alt = image.alt;
      openDialog("ZoomDialog", button);
    } else if (button.matches("[data-remove]")) {
      event.preventDefault();
      mutateCart("cart/change.js", { id: button.dataset.remove, quantity: 0 });
    } else if (button.matches("[data-refresh-cart]")) refreshCart();
  });
  document.addEventListener("change", (event) => {
    const input = event.target;
    if (input.matches("[data-line-key]")) {
      if (!input.checkValidity()) {
        input.reportValidity();
        return;
      }
      mutateCart("cart/change.js", {
        id: input.dataset.lineKey,
        quantity: Number(input.value),
      });
    } else if (input.matches("[data-sort]")) {
      const url = new URL(location.href);
      url.searchParams.set("sort_by", input.value);
      url.searchParams.delete("page");
      location.assign(url.href);
    }
  });
  document.addEventListener("input", (event) => {
    if (event.target.matches("[data-predictive]")) {
      clearTimeout(predictiveTimer);
      const input = event.target;
      predictiveTimer = setTimeout(() => predictive(input), 200);
    }
  });
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (form.matches(".product-form")) {
      event.preventDefault();
      if (state.cartBusy) return;
      const data = new FormData(form),
        id = Number(data.get("id")),
        quantity = Number(data.get("quantity") || 1);
      if (
        !Number.isSafeInteger(id) ||
        id <= 0 ||
        !Number.isInteger(quantity) ||
        quantity <= 0
      )
        return;
      const properties = {};
      for (const [key, value] of data.entries())
        if (key.startsWith("properties["))
          properties[key.slice(11, -1)] = value;
      mutateCart(
        "cart/add.js",
        { items: [{ id, quantity, properties }] },
        true,
      );
    } else if (form.matches(".filter-form")) {
      const ranges = $$(".price-range input", form),
        min = Number(ranges[0]?.value || 0),
        max = Number(ranges[1]?.value || Infinity);
      if (min > max) {
        event.preventDefault();
        const error = $(".form-error", form);
        error.textContent = "The minimum price must be lower than the maximum.";
        error.hidden = false;
        ranges[0].focus();
      } else
        $$("input", form)
          .filter((input) => input.value === "")
          .forEach((input) => {
            input.disabled = true;
          });
    } else if (form.matches("[data-cart-form]") && state.cartBusy)
      event.preventDefault();
  });
  window.addEventListener("storage", (event) => {
    if (event.key === savedKey) {
      syncSaved();
      if ($("#SavedDialog")?.open) renderSaved();
    }
  });
  window.addEventListener("pageshow", (event) => {
    if (event.persisted) {
      syncSaved();
      refreshCart();
    }
  });
  window.addEventListener("popstate", () => {
    if ($("arc-product:not([data-quick])")) location.reload();
  });
  document.addEventListener("shopify:section:load", (event) =>
    init(event.target),
  );
  document.addEventListener("shopify:section:unload", (event) => {
    $$("dialog[open]", event.target).forEach(closeDialog);
  });
  document.addEventListener("shopify:section:reorder", () => syncSaved());
  init();
})();
